/**
 * Bundle Tiered Selector — vanilla JS behavior.
 *
 * Ports the interaction/state machine from
 * docs/task/reference/konjoun-offer-builder-v3-mockup.html to the real
 * Liquid markup rendered by snippets/bundle-tiered-selector.liquid and
 * snippets/bundle-tiered-selector-version.liquid. See
 * _workspace/02_liquid_dev_output.md (section 7) for the full data-attribute
 * contract this file reads, and _workspace/03_frontend_output.md for the
 * frontend implementation notes / discrepancies found while wiring this up.
 *
 * No dependency on jQuery/Alpine/Vue. No async gating before click handlers
 * are attached — every listener is bound synchronously in connectedCallback.
 */
(function () {
  'use strict';

  // Used by portalOverlayToBody() (both the pair-customizer modal and the
  // size-guide modal) to keep looking correct once moved to <body>, outside
  // .bts-root's --bts-* custom-property scope. Declared here — not next to
  // that function further down — because customElements.define() can
  // synchronously fire connectedCallback -> initRoot -> ...-> this before
  // the file finishes executing top-to-bottom; a `var` assigned later would
  // still be undefined at that point.
  var BTS_PORTAL_VARS = [
    '--bts-radius-lg', '--bts-radius-md', '--bts-radius-sm', '--bts-radius-pill',
    '--bts-ink', '--bts-muted', '--bts-surface', '--bts-soft', '--bts-selected',
    '--bts-line', '--bts-action', '--bts-action-ink', '--bts-overlay',
    '--bts-space-1', '--bts-space-2', '--bts-space-3', '--bts-space-4', '--bts-space-5'
  ];

  /* ----------------------------------------------------------------------
   * Small DOM helpers
   * ------------------------------------------------------------------- */

  function qs(root, selector) {
    return root.querySelector(selector);
  }

  function qsa(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  function setPressed(el, isActive, activeClass) {
    if (!el) return;
    el.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    if (activeClass) {
      el.classList.toggle(activeClass, isActive);
    }
  }

  /* ----------------------------------------------------------------------
   * Version-data helpers (reading the `.bts-version-data` JSON island)
   * ------------------------------------------------------------------- */

  function findColor(versionData, name) {
    var colors = (versionData && versionData.colors) || [];
    for (var i = 0; i < colors.length; i += 1) {
      if (colors[i].name === name) return colors[i];
    }
    return null;
  }

  function findFirstAvailableSize(colorData) {
    if (!colorData || !colorData.sizes || !colorData.sizes.length) return '';
    for (var i = 0; i < colorData.sizes.length; i += 1) {
      if (colorData.sizes[i].available) return colorData.sizes[i].name;
    }
    return colorData.sizes[0].name;
  }

  // Finds the REAL variant matching Pair 1's chosen color/size for the active
  // tier, instead of always using the arbitrary "representative" variant
  // Liquid picked at page load (see docs/task/bundle-tiered-selector-plan.md
  // section 3.1). Using the real match means: the cart line item's image/
  // title reflects the color the customer actually picked (QA: "product
  // image doesn't match red & blue"), and availability is checked against
  // that color/size instead of a fixed one.
  function findVariant(versionData, color, size, tierName) {
    var variants = (versionData && versionData.variants) || [];
    var idx = (versionData && versionData.optionIndex) || {};
    for (var i = 0; i < variants.length; i += 1) {
      var v = variants[i];
      var opts = v.options || [];
      var colorMatches = idx.color < 0 || opts[idx.color] === color;
      var sizeMatches = idx.size < 0 || opts[idx.size] === size;
      var tierMatches = opts[idx.bundleTier] === tierName;
      if (colorMatches && sizeMatches && tierMatches) {
        return v;
      }
    }
    return null;
  }

  /* ----------------------------------------------------------------------
   * Custom element
   * ------------------------------------------------------------------- */

  // Custom elements upgrade (and connectedCallback fires) SYNCHRONOUSLY the
  // moment `customElements.define` runs below, for any matching element
  // already in the DOM at that point — i.e. while this IIFE is still
  // executing top-to-bottom, before later `var` statements in this file have
  // been assigned their value (only the declaration hoists). initRoot must
  // not depend on anything declared further down the file, or that access
  // throws mid-init and silently aborts the rest of initRoot (every listener
  // scheduled after the throw — every tier card, swatch, size button —
  // never gets attached). BTS_PORTAL_VARS used to be declared next to
  // portalOverlayToBody() near the bottom of the file for exactly that
  // reason and tripped this; it now lives with the other module-level
  // constants above.
  class BundleTieredSelector extends HTMLElement {
    connectedCallback() {
      if (this.dataset.btsInitialized === 'true') return;
      this.dataset.btsInitialized = 'true';
      initRoot(this);
    }
  }

  if (!customElements.get('bundle-tiered-selector-root')) {
    customElements.define('bundle-tiered-selector-root', BundleTieredSelector);
  }

  /* ----------------------------------------------------------------------
   * Root-level init
   * ------------------------------------------------------------------- */

  function initRoot(root) {
    var defaultHandle = root.getAttribute('data-default-version-handle') || '';
    root._btsDefaultHandle = defaultHandle;
    root._btsActiveHandle = defaultHandle;

    var localeEl = qs(root, '[data-bts-locale]');
    root._btsLocale = {
      colorLabel: (localeEl && localeEl.getAttribute('data-color-label-template')) || 'Color: {color}',
      sizeLabel: (localeEl && localeEl.getAttribute('data-size-label-template')) || 'Size: {size}',
      modalPairTitle: (localeEl && localeEl.getAttribute('data-modal-pair-title-template')) || 'Pair {number} · Size: {size}',
      noMatch: (localeEl && localeEl.getAttribute('data-no-match-message')) || 'Unavailable'
    };

    // Version switch buttons (segmented control) — root level, shared by all versions.
    qsa(root, '[data-bts-version-btn]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        switchVersion(root, btn.getAttribute('data-version-handle'));
      });
    });

    // Size-guide anchors: currently `href="#"` with no page wired up by Liquid
    // Liquid renders a real href from custom.bundle_tier_size_guide_page when
    // the merchant sets it; only suppress the click when that metafield is
    // unset and the link still falls back to the dead "#" target.
    // When the page HAS content, Liquid also stamps [data-bts-size-guide-open]
    // and renders [data-bts-size-guide-overlay]: open that in-page modal
    // instead of navigating away.
    root._btsSizeGuideOverlay = qs(root, '[data-bts-size-guide-overlay]');
    qsa(root, '[data-bts-size-guide-trigger], [data-bts-modal-size-guide-trigger]').forEach(function (link) {
      link.addEventListener('click', function (event) {
        if (link.hasAttribute('data-bts-size-guide-open') && root._btsSizeGuideOverlay) {
          event.preventDefault();
          openSizeGuide(root);
          return;
        }
        if (link.getAttribute('href') === '#') {
          event.preventDefault();
        }
      });
    });

    // Modal shell (single instance, shared by every version).
    root._btsModal = {
      overlay: qs(root, '[data-bts-modal-overlay]'),
      closeBtn: qs(root, '[data-bts-modal-close]'),
      gallery: qs(root, '[data-bts-modal-gallery]'),
      title: qs(root, '[data-bts-modal-title]'),
      colorLabel: qs(root, '[data-bts-modal-color-label]'),
      sizes: qs(root, '[data-bts-modal-sizes]'),
      colors: qs(root, '[data-bts-modal-colors]'),
      actionBtn: qs(root, '[data-bts-modal-action]')
    };
    if (root._btsModal.closeBtn) {
      root._btsModal.closeBtn.addEventListener('click', function () {
        closeModal(root);
      });
    }
    if (root._btsModal.actionBtn) {
      root._btsModal.actionBtn.addEventListener('click', function () {
        saveModalDraft(root);
      });
    }
    if (root._btsModal.overlay) {
      root._btsModal.overlay.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' || event.key === 'Esc') {
          closeModal(root);
        }
      });
      // Portal the overlay to <body>. main-product's sticky info column
      // establishes a new containing block (transform/will-change) for its
      // descendants, which silently turns this element's `position: fixed`
      // into "fixed relative to that column" instead of the viewport — the
      // modal rendered pinned to the right column instead of centered over
      // the whole page (QA: "should be center of the screen with same black
      // overlay"). Moving the node itself out from under that ancestor is
      // the only reliable fix without touching main-product.liquid.
      //
      // The --bts-* custom properties are declared on `.bts-root` (they in
      // turn read this theme's --color-*/--buttons-radius tokens, which may
      // only be defined inside a color-scheme wrapper .bts-root sits under —
      // not guaranteed to reach <body>). Once the overlay is no longer a
      // descendant of .bts-root it stops inheriting them and renders fully
      // transparent. Copy each RESOLVED value onto the overlay's own inline
      // style before moving it, so it keeps looking correct regardless of
      // where in the DOM it ends up.
      var BTS_VARS = [
        '--bts-radius-lg', '--bts-radius-md', '--bts-radius-sm', '--bts-radius-pill',
        '--bts-ink', '--bts-muted', '--bts-surface', '--bts-soft', '--bts-selected',
        '--bts-line', '--bts-action', '--bts-action-ink', '--bts-overlay',
        '--bts-space-1', '--bts-space-2', '--bts-space-3', '--bts-space-4', '--bts-space-5'
      ];
      var rootStyle = getComputedStyle(root);
      BTS_VARS.forEach(function (name) {
        root._btsModal.overlay.style.setProperty(name, rootStyle.getPropertyValue(name));
      });
      document.body.appendChild(root._btsModal.overlay);
    }

    initSizeGuide(root);

    root._btsDynamicCheckout = qs(root, '[data-bts-dynamic-checkout]');

    // Init every version block found inside this root.
    var versionEls = qsa(root, '[data-bts-version]');
    versionEls.forEach(function (versionEl) {
      initVersion(root, versionEl);
    });

    // The surrounding product form's `input[name="id"]` is set by Liquid to
    // whatever `product.selected_or_first_available_variant` resolves to,
    // which is not guaranteed to be the "1 Pair" Bundle Tier variant that is
    // active by default in this widget — sync it once on load so Add to
    // Cart / the dynamic checkout button start out pointed at the right
    // variant even before the shopper clicks anything.
    var defaultVersionEl = versionEls.filter(function (el) {
      return el.getAttribute('data-version-handle') === defaultHandle;
    })[0];
    if (defaultVersionEl && defaultVersionEl._btsState && defaultVersionEl._btsState.activeOffer) {
      updateVariantInput(root, defaultVersionEl._btsState.activeOffer);
      window.requestAnimationFrame(function () {
              var activeOffer = root.querySelector('[data-bts-offer][aria-pressed="true"]') || root.querySelector('[data-bts-offer].bts-offer--active') || defaultVersionEl._btsState.activeOffer;

            syncBundlePriceDisplays(root, activeOffer);
          });
    }
  window.setTimeout(function () {
         var activeOffer = defaultVersionEl && defaultVersionEl._btsState && defaultVersionEl._btsState.activeOffer;
   if (activeOffer) syncBundlePriceDisplays(root, activeOffer);
  }, 2000);

    }

  /* ----------------------------------------------------------------------
   * Per-version init
   * ------------------------------------------------------------------- */

  
  function initVersion(root, versionEl) {
    var dataScript = versionEl.querySelector('.bts-version-data');
    var versionData = { colors: [], defaultColor: '', defaultSize: '' };
    if (dataScript) {
      try {
        versionData = JSON.parse(dataScript.textContent);
      } catch (parseError) {
        // Keep the empty fallback — tier selection/pricing does not depend
        // on this data (only shared color/size + the modal do), so a
        // malformed island degrades gracefully instead of breaking the
        // whole widget.
      }
    }

    var refs = {
      offerButtons: qsa(versionEl, '[data-bts-offer]'),
      sharedOptionsEl: qs(versionEl, '[data-bts-shared-options]'),
      sharedColorLabelEl: qs(versionEl, '[data-bts-shared-color-label]'),
      sharedColorButtons: qsa(versionEl, '[data-bts-shared-color-option]'),
      sharedSizeLabelEl: qs(versionEl, '[data-bts-shared-size-label]'),
      sharedSizeButtons: qsa(versionEl, '[data-bts-shared-size-option]'),
      customizeToggle: qs(versionEl, '[data-bts-customize-toggle]'),
      customPanelEl: qs(versionEl, '[data-bts-custom-panel]'),
      bundleCountEl: qs(versionEl, '[data-bts-bundle-count]'),
      slotsEl: qs(versionEl, '[data-bts-slots]'),
      slotButtons: qsa(versionEl, '[data-bts-slot]'),
      summarySingleEl: qs(versionEl, '[data-bts-summary-single]'),
      summaryBundleEl: qs(versionEl, '[data-bts-summary-bundle]'),
      savingsNoteEls: qsa(versionEl, '[data-bts-savings-note]'),
      totalPriceEls: qsa(versionEl, '[data-bts-total-price]'),
      originalPriceEls: qsa(versionEl, '[data-bts-original-price]'),
      mainActionBtn: qs(versionEl, '[data-bts-main-action]'),
      mainActionLabelEl: qs(versionEl, '[data-bts-main-action-label]')
    };

    var initialOffer = refs.offerButtons.filter(function (btn) {
      return btn.getAttribute('aria-pressed') === 'true';
    })[0] || refs.offerButtons[0];

    var state = {
      versionData: versionData,
      refs: refs,
      activeOffer: initialOffer,
      pairCount: initialOffer ? Number(initialOffer.getAttribute('data-tier-pair-count')) || 1 : 1,
      single: { color: versionData.defaultColor || '', size: versionData.defaultSize || '' },
      customEnabled: false,
      slots: []
    };
    versionEl._btsState = state;

    refs.offerButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        selectTier(root, versionEl, btn);
      });
    });

    refs.sharedColorButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        selectSharedColor(root, versionEl, btn.getAttribute('data-color-name'));
      });
    });

    refs.sharedSizeButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        selectSharedSize(root, versionEl, btn.getAttribute('data-size-name'));
      });
    });

    if (refs.customizeToggle) {
      refs.customizeToggle.addEventListener('click', function () {
        toggleCustomize(root, versionEl);
      });
    }

    refs.slotButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var index = Number(btn.getAttribute('data-slot-index'));
        openModal(root, versionEl, index);
      });
    });

    if (refs.mainActionBtn) {
      refs.mainActionBtn.addEventListener('click', function () {
        addToCart(root, versionEl);
      });
    }

    // Defensive parity pass: recompute everything JS is responsible for so
    // it never silently drifts from what Liquid rendered server-side.
    syncLabelsForActiveOffer(root, versionEl);
    updateSharedSizeButtons(versionEl);
    applySharedOptionLabels(root, versionEl);
    renderCustomizeVisibility(versionEl);
    renderSharedOptionsVisibility(versionEl);
    renderSlots(root, versionEl);
  }

  /* ----------------------------------------------------------------------
   * Tier selection
   * ------------------------------------------------------------------- */

  function selectTier(root, versionEl, offerBtn) {
    var state = versionEl._btsState;
    if (offerBtn === state.activeOffer) return;

    state.refs.offerButtons.forEach(function (btn) {
      setPressed(btn, btn === offerBtn, 'bts-offer--active');
    });
    state.activeOffer = offerBtn;
    state.pairCount = Number(offerBtn.getAttribute('data-tier-pair-count')) || 1;
    state.customEnabled = false;
    state.slots = [];

    closeModal(root);
    syncLabelsForActiveOffer(root, versionEl);
    updateVariantInput(root, offerBtn);
    renderCustomizeVisibility(versionEl);
    renderSharedOptionsVisibility(versionEl);
    renderSlots(root, versionEl);
  }

  function syncLabelsForActiveOffer(root, versionEl) {
    var state = versionEl._btsState;
    var btn = state.activeOffer;
    if (!btn) return;

    var isSingle = state.pairCount === 1;
    if (state.refs.summarySingleEl) state.refs.summarySingleEl.hidden = !isSingle;
    if (state.refs.summaryBundleEl) state.refs.summaryBundleEl.hidden = isSingle;

    var savingsNote = btn.getAttribute('data-tier-savings-note') || '';
    state.refs.savingsNoteEls.forEach(function (el) {
      el.textContent = savingsNote;
    });

    // Pre-formatted server-side via the `money` filter (see
    // snippets/bundle-tiered-selector-version.liquid) so this always matches
    // the shop's real money_format, in any locale/currency.
    var formattedPrice = btn.getAttribute('data-tier-price-money') || '';
    state.refs.totalPriceEls.forEach(function (el) {
      el.textContent = formattedPrice;
    });

    if (!isSingle) {
      var formattedOriginal = btn.getAttribute('data-tier-original-price-money') || '';
      state.refs.originalPriceEls.forEach(function (el) {
        el.textContent = formattedOriginal;
      });
    }

    if (state.refs.bundleCountEl) {
      state.refs.bundleCountEl.textContent = btn.getAttribute('data-tier-pairs-label') || '';
    }

    var idleLabel = btn.getAttribute('data-tier-action-label') || '';
    var isAvailable = btn.getAttribute('data-tier-available') !== 'false';
    var hasVariant = !!btn.getAttribute('data-tier-variant-id');
    if (state.refs.mainActionBtn) {
      state.refs.mainActionBtn.setAttribute('data-add-label', idleLabel);
      state.refs.mainActionBtn.disabled = !isAvailable || !hasVariant;
    }
    if (state.refs.mainActionLabelEl) {
      state.refs.mainActionLabelEl.textContent = idleLabel;
    }
  }

  function renderCustomizeVisibility(versionEl) {
    var state = versionEl._btsState;
    var toggle = state.refs.customizeToggle;
    if (!toggle) return;
    toggle.hidden = state.pairCount <= 1;
    toggle.setAttribute('aria-pressed', state.customEnabled ? 'true' : 'false');
    toggle.setAttribute('aria-checked', state.customEnabled ? 'true' : 'false');
  }

  function renderSharedOptionsVisibility(versionEl) {
    var state = versionEl._btsState;
    // Explicit product requirement: while "Customize each pair" is on, the
    // shared color/size picker must be fully hidden (not just disabled).
    if (state.refs.sharedOptionsEl) state.refs.sharedOptionsEl.hidden = state.customEnabled;
    if (state.refs.customPanelEl) state.refs.customPanelEl.hidden = !state.customEnabled;
  }

  /* ----------------------------------------------------------------------
   * Shared color/size (used when customize is off, or for qty === 1)
   * ------------------------------------------------------------------- */

  // function selectSharedColor(root, versionEl, colorName) {
  //   if (!colorName) return;
  //   var state = versionEl._btsState;
  //   var colorData = findColor(state.versionData, colorName);

  //   state.single.color = colorName;
  //   state.single.size = findFirstAvailableSize(colorData);

  //   state.refs.sharedColorButtons.forEach(function (btn) {
  //     setPressed(btn, btn.getAttribute('data-color-name') === colorName, 'bts-swatch--active');
  //   });

  //   updateSharedSizeButtons(versionEl);
  //   applySharedOptionLabels(root, versionEl);

  //   if (state.customEnabled) {
  //     syncSlotsToShared(state);
  //     renderSlots(root, versionEl);
  //   }
  // }


  function selectSharedColor(root, versionEl, colorName) {
    if (!colorName) return;

    var state = versionEl._btsState;
    var previousColor = state.single.color;

    /* Do nothing when the already-selected color is clicked. */
    if (colorName === previousColor) return;

    var colorData = findColor(state.versionData, colorName);

    state.single.color = colorName;
    state.single.size = findFirstAvailableSize(colorData);

    state.refs.sharedColorButtons.forEach(function (btn) {
      setPressed(
        btn,
        btn.getAttribute('data-color-name') === colorName,
        'bts-swatch--active'
      );
    });

    updateSharedSizeButtons(versionEl);
    applySharedOptionLabels(root, versionEl);

    if (state.customEnabled) {
      syncSlotsToShared(state);
      renderSlots(root, versionEl);
      return;
    }

    /*
    * Only update the main gallery after a manual shared-color selection.
    * This function is not called during initial page setup, so the landing
    * gallery remains unchanged.
    */
    updateMainGalleryForColor(root, versionEl);
  }


/**
 * Moves media whose alt text exactly matches the selected color
 * to the beginning of the main gallery and thumbnail gallery.
 */
function moveMatchingImagesFirst(gallery, colorName) {
  if (!gallery || !colorName) return;

  var normalizedColor = colorName
    .trim()
    .toLowerCase();

  function reorder(container) {
    if (!container) return;

    var items = Array.prototype.slice.call(container.children);

    var matchingItems = [];
    var remainingItems = [];

    items.forEach(function (item) {
      var mediaAlt = item.getAttribute('data-media-alt');
      var image = item.querySelector('img[alt]');

      if (!mediaAlt && image) {
        mediaAlt = image.getAttribute('alt');
      }

      var normalizedAlt = (mediaAlt || '')
        .trim()
        .toLowerCase();

      if (normalizedAlt === normalizedColor) {
        matchingItems.push(item);
      } else {
        remainingItems.push(item);
      }
    });

    matchingItems
      .concat(remainingItems)
      .forEach(function (item, index) {
        container.appendChild(item);

        /*
         * Keep gallery position metadata synchronized with
         * the new visual order.
         */
        if (item.hasAttribute('data-media-position')) {
          item.setAttribute(
            'data-media-position',
            String(index + 1)
          );
        }
      });
  }

  reorder(
    gallery.querySelector('[id^="Slider-Gallery-"]')
  );

  reorder(
    gallery.querySelector('[id^="Slider-Thumbnails-"]')
  );
}


/**
 * Reload only the main product gallery using the selected color's variant.
 * product-media-gallery-hhd.liquid handles the alt-text image filtering.
 */
function updateMainGalleryForColor(root, versionEl) {
  var state = versionEl._btsState;

  if (!state || state.customEnabled || !state.activeOffer) return;

  var tierName =
    state.activeOffer.getAttribute('data-tier-name') || '';

  var matchedVariant = findVariant(
    state.versionData,
    state.single.color,
    state.single.size,
    tierName
  );

  if (!matchedVariant || !matchedVariant.id) return;

  var mainProductSection =
    root.closest('[id^="MainProduct-"][data-section]');

  if (!mainProductSection) return;

  var sectionId =
    mainProductSection.getAttribute('data-section');

  var versionHandle =
    versionEl.getAttribute('data-version-handle') ||
    root._btsActiveHandle ||
    root._btsDefaultHandle;

  if (!sectionId || !versionHandle) return;

  var shopRoot =
    window.Shopify &&
    window.Shopify.routes &&
    window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

  if (shopRoot.charAt(shopRoot.length - 1) !== '/') {
    shopRoot += '/';
  }

  var requestUrl = new URL(
    shopRoot + 'products/' + encodeURIComponent(versionHandle),
    window.location.origin
  );

  /*
   * Preserve theme-preview parameters while testing on an unpublished
   * Shopify theme.
   */
  var currentParams = new URLSearchParams(window.location.search);

  currentParams.forEach(function (value, key) {
    requestUrl.searchParams.set(key, value);
  });

  requestUrl.searchParams.set('variant', matchedVariant.id);
  requestUrl.searchParams.set('section_id', sectionId);

  /*
   * Prevent an older request from replacing the gallery if the customer
   * clicks multiple colors quickly.
   */
  var requestId = (root._btsGalleryRequestId || 0) + 1;
  root._btsGalleryRequestId = requestId;

  var currentGallery =
    mainProductSection.querySelector('media-gallery');

  if (currentGallery) {
    currentGallery.setAttribute('aria-busy', 'true');
  }

  fetch(requestUrl.toString(), {
    credentials: 'same-origin'
  })
    .then(function (response) {
      if (!response.ok) {
        throw new Error('Unable to load the selected color gallery.');
      }

      return response.text();
    })
    .then(function (sectionHtml) {
      if (requestId !== root._btsGalleryRequestId) return;

      var parsedDocument =
        new DOMParser().parseFromString(sectionHtml, 'text/html');

      var replacementGallery =
        parsedDocument.querySelector('media-gallery');

      var liveGallery =
        mainProductSection.querySelector('media-gallery');

      if (!replacementGallery || !liveGallery) return;

      /* Move images matching the selected color to the beginning. */
      moveMatchingImagesFirst(
        replacementGallery,
        state.single.color
      );

      replacementGallery.removeAttribute('aria-busy');
      liveGallery.replaceWith(replacementGallery);

      /* Scroll to the updated product gallery after manual color selection. */
      window.requestAnimationFrame(function () {
        var header = document.querySelector(
          'sticky-header, .header-wrapper, header'
        );

        var headerHeight = header
          ? header.getBoundingClientRect().height
          : 0;

        var galleryTop =
          replacementGallery.getBoundingClientRect().top +
          window.pageYOffset;

        window.scrollTo({
          top: Math.max(0, galleryTop - headerHeight - 12),
          behavior: 'smooth'
        });
      });
    })
    .catch(function (error) {
      if (requestId !== root._btsGalleryRequestId) return;

      var liveGallery =
        mainProductSection.querySelector('media-gallery');

      if (liveGallery) {
        liveGallery.removeAttribute('aria-busy');
      }

      console.warn('[Bundle Tiered Selector]', error);
    });
}

  function selectSharedSize(root, versionEl, sizeName) {
    if (!sizeName) return;
    var state = versionEl._btsState;
    state.single.size = sizeName;

    state.refs.sharedSizeButtons.forEach(function (btn) {
      setPressed(btn, btn.getAttribute('data-size-name') === sizeName, 'bts-size--active');
    });
    applySharedOptionLabels(root, versionEl);

    if (state.customEnabled) {
      syncSlotsToShared(state);
      renderSlots(root, versionEl);
    }
  }

  function updateSharedSizeButtons(versionEl) {
    var state = versionEl._btsState;
    var colorData = findColor(state.versionData, state.single.color);
    var availabilityByName = {};
    (colorData && colorData.sizes || []).forEach(function (size) {
      availabilityByName[size.name] = size.available;
    });

    state.refs.sharedSizeButtons.forEach(function (btn) {
      var name = btn.getAttribute('data-size-name');
      // No entry for this size in the color's data = treat as unavailable
      // rather than assuming available, so a malformed/partial data island
      // degrades to "nothing sellable" instead of silently allowing an
      // invalid color/size combination through to the cart.
      var available = !!availabilityByName[name];
      btn.disabled = !available;
      btn.classList.toggle('bts-size--unavailable', !available);
      setPressed(btn, name === state.single.size, 'bts-size--active');
    });
  }

  function applySharedOptionLabels(root, versionEl) {
    var state = versionEl._btsState;
    if (state.refs.sharedColorLabelEl) {
      state.refs.sharedColorLabelEl.textContent = root._btsLocale.colorLabel.replace('{color}', state.single.color);
    }
    if (state.refs.sharedSizeLabelEl) {
      state.refs.sharedSizeLabelEl.textContent = root._btsLocale.sizeLabel.replace('{size}', state.single.size);
    }
  }

  function syncSlotsToShared(state) {
    state.slots = [];
    for (var i = 0; i < state.pairCount; i += 1) {
      state.slots.push({ color: state.single.color, size: state.single.size });
    }
  }

  /* ----------------------------------------------------------------------
   * Customize toggle + per-pair slots
   * ------------------------------------------------------------------- */

  function toggleCustomize(root, versionEl) {
    var state = versionEl._btsState;
    state.customEnabled = !state.customEnabled;
    if (state.customEnabled) {
      syncSlotsToShared(state);
    } else {
      state.slots = [];
    }

    closeModal(root);
    renderCustomizeVisibility(versionEl);
    renderSharedOptionsVisibility(versionEl);
    renderSlots(root, versionEl);
  }

  function renderSlots(root, versionEl) {
    var state = versionEl._btsState;
    var visibleCount = state.customEnabled ? state.pairCount : 0;

    if (state.refs.slotsEl) {
      // Liquid pre-renders --bts-slot-count as a fixed max (the tier with
      // the most pairs). Keep the grid tight to the pairs actually shown so
      // a 2-pair tier does not leave a dead empty column in a 3-col grid —
      // see _workspace/03_frontend_output.md for the full note.
      var desktopCount = Math.min(visibleCount || 1, 6);
      var mobileCount = Math.min(visibleCount || 1, 3);
      state.refs.slotsEl.style.setProperty('--bts-slot-count', String(desktopCount));
      state.refs.slotsEl.style.setProperty('--bts-mobile-slot-count', String(mobileCount));
    }

    state.refs.slotButtons.forEach(function (btn, index) {
      var isVisible = index < visibleCount;
      btn.hidden = !isVisible;
      if (!isVisible) return;

      var slot = state.slots[index];
      if (!slot) return;

      var swatchEl = btn.querySelector('[data-bts-slot-swatch]');
      var colorEl = btn.querySelector('[data-bts-slot-color]');
      var sizeEl = btn.querySelector('[data-bts-slot-size]');
      var colorData = findColor(state.versionData, slot.color);

      if (swatchEl) {
        // Only a real native-swatch color is a valid CSS color. Falling back
        // to the option name painted "Army Green" into background-color,
        // which computes to nothing and left an empty dot. With no color,
        // drop the property so CSS falls back to var(--bts-soft).
        var swatchColor = colorData && colorData.swatch && colorData.swatch.color;
        if (swatchColor) {
          swatchEl.style.setProperty('--bts-swatch-color', swatchColor);
        } else {
          swatchEl.style.removeProperty('--bts-swatch-color');
        }
        var swatchImage = colorData && colorData.swatch && colorData.swatch.image;
        if (swatchImage) {
          swatchEl.style.setProperty('--bts-swatch-image', 'url(' + swatchImage + ')');
        } else {
          swatchEl.style.removeProperty('--bts-swatch-image');
        }
      }
      if (colorEl) colorEl.textContent = slot.color;
      if (sizeEl) sizeEl.textContent = root._btsLocale.sizeLabel.replace('{size}', slot.size);
    });
  }

  /* ----------------------------------------------------------------------
   * Modal (shared overlay, edits one slot at a time)
   * ------------------------------------------------------------------- */

  function openModal(root, versionEl, index) {
    var state = versionEl._btsState;
    var current = state.slots[index];
    var draft = current && current.color
      ? { color: current.color, size: current.size }
      : { color: state.single.color, size: state.single.size };

    root._btsModalContext = { versionEl: versionEl, index: index, draft: draft };
    renderModal(root);
    if (root._btsModal.overlay) {
      root._btsModal.overlay.hidden = false;
    }
    if (root._btsModal.closeBtn && typeof root._btsModal.closeBtn.focus === 'function') {
      root._btsModal.closeBtn.focus();
    }
  }

  function closeModal(root) {
    if (root._btsModal && root._btsModal.overlay) {
      root._btsModal.overlay.hidden = true;
    }
    // Force the image slider to rebuild (back at image 1) on the next open.
    if (root._btsModal && root._btsModal.gallery) {
      root._btsModal.gallery._btsGalleryKey = null;
    }
    root._btsModalContext = null;
  }

  /* ----------------------------------------------------------------------
   * Size guide modal (in-page popup for custom.bundle_tier_size_guide_page)
   * ------------------------------------------------------------------- */

  function portalOverlayToBody(root, overlay) {
    var rootStyle = getComputedStyle(root);
    BTS_PORTAL_VARS.forEach(function (name) {
      overlay.style.setProperty(name, rootStyle.getPropertyValue(name));
    });
    document.body.appendChild(overlay);
  }

  function initSizeGuide(root) {
    var overlay = root._btsSizeGuideOverlay;
    if (!overlay) return;

    var closeBtn = qs(overlay, '[data-bts-size-guide-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        closeSizeGuide(root);
      });
    }
    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) closeSizeGuide(root);
    });
    overlay.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' || event.key === 'Esc') closeSizeGuide(root);
    });

    portalOverlayToBody(root, overlay);
  }

  function openSizeGuide(root) {
    var overlay = root._btsSizeGuideOverlay;
    if (!overlay) return;
    overlay.hidden = false;
    var closeBtn = qs(overlay, '[data-bts-size-guide-close]');
    if (closeBtn && typeof closeBtn.focus === 'function') {
      closeBtn.focus();
    } else if (typeof overlay.focus === 'function') {
      overlay.focus();
    }
  }

  function closeSizeGuide(root) {
    if (root._btsSizeGuideOverlay) {
      root._btsSizeGuideOverlay.hidden = true;
    }
  }

  function renderModal(root) {
    var ctx = root._btsModalContext;
    if (!ctx) return;
    var state = ctx.versionEl._btsState;
    var modal = root._btsModal;
    var colorData = findColor(state.versionData, ctx.draft.color);

    if (modal.gallery) {
      // Only rebuild the slider when the colour actually changed, so that
      // picking a size does not yank the shopper back to the first image.
      // A colour change intentionally resets the slider to image 1.
      var galleryKey = ctx.draft.color || '';
      if (modal.gallery._btsGalleryKey !== galleryKey || !modal.gallery.childNodes.length) {
        modal.gallery._btsGalleryKey = galleryKey;
        renderModalGallery(modal.gallery, colorData, galleryKey);
      }
    }

    if (modal.title) {
      modal.title.textContent = root._btsLocale.modalPairTitle
        .replace('{number}', String(ctx.index + 1))
        .replace('{size}', ctx.draft.size || '');
    }
    if (modal.colorLabel) {
      modal.colorLabel.textContent = root._btsLocale.colorLabel.replace('{color}', ctx.draft.color || '');
    }

    if (modal.sizes) {
      modal.sizes.innerHTML = '';
      (colorData && colorData.sizes || []).forEach(function (size) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bts-size' +
          (size.name === ctx.draft.size ? ' bts-size--active' : '') +
          (!size.available ? ' bts-size--unavailable' : '');
        btn.setAttribute('data-modal-size-name', size.name);
        btn.setAttribute('aria-pressed', size.name === ctx.draft.size ? 'true' : 'false');
        if (!size.available) btn.disabled = true;
        btn.textContent = size.name;
        btn.addEventListener('click', function () {
          if (btn.disabled) return;
          ctx.draft.size = size.name;
          renderModal(root);
        });
        modal.sizes.appendChild(btn);
      });
    }

    if (modal.colors) {
      modal.colors.innerHTML = '';
      (state.versionData.colors || []).forEach(function (color) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bts-swatch' +
          (color.name === ctx.draft.color ? ' bts-swatch--active' : '') +
          (!color.available ? ' bts-swatch--unavailable' : '');
        btn.setAttribute('data-modal-color-name', color.name);
        btn.setAttribute('data-color-available', color.available ? 'true' : 'false');
        btn.setAttribute('aria-pressed', color.name === ctx.draft.color ? 'true' : 'false');
        btn.setAttribute('aria-label', color.name);
        if (!color.available) btn.disabled = true;
        // See renderSlots: the option name is not a CSS color, so only set
        // --bts-swatch-color when the merchant set a native swatch color.
        // The image (now alt-text matched in Liquid) carries the rest.
        var swatchColor = color.swatch && color.swatch.color;
        if (swatchColor) {
          btn.style.setProperty('--bts-swatch-color', swatchColor);
        }
        if (color.swatch && color.swatch.image) {
          btn.style.setProperty('--bts-swatch-image', 'url(' + color.swatch.image + ')');
        }
        btn.addEventListener('click', function () {
          if (btn.disabled) return;
          ctx.draft.color = color.name;
          ctx.draft.size = findFirstAvailableSize(color);
          renderModal(root);
        });
        modal.colors.appendChild(btn);
      });
    }
  }

  /**
   * Build the modal image slider for one colour.
   *
   * colorData.images holds every product image whose alt text matches the
   * colour (Liquid side). Falls back to the legacy single `image` field so
   * this keeps working if the Liquid change is not deployed yet.
   * 0 images -> placeholder frame, 1 image -> plain frame with no slider
   * chrome, 2+ -> scroll-snap track with arrows + dots.
   */
  function renderModalGallery(gallery, colorData, colorName) {
    gallery.innerHTML = '';
    gallery.classList.remove('bts-slider');

    var images = (colorData && colorData.images) || [];
    if (!images.length && colorData && colorData.image) images = [colorData.image];

    if (images.length < 2) {
      var single = document.createElement('div');
      single.className = 'bts-modal__gallery-item';
      if (images.length) {
        var onlyImg = document.createElement('img');
        onlyImg.src = images[0];
        onlyImg.alt = colorName;
        onlyImg.loading = 'lazy';
        single.appendChild(onlyImg);
      } else {
        single.classList.add('bts-modal__gallery-item--empty');
      }
      gallery.appendChild(single);
      return;
    }

    gallery.classList.add('bts-slider');

    var track = document.createElement('div');
    track.className = 'bts-slider__track';
    images.forEach(function (url, i) {
      var slide = document.createElement('div');
      slide.className = 'bts-modal__gallery-item bts-slider__slide';
      var img = document.createElement('img');
      img.src = url;
      img.alt = colorName + ' ' + (i + 1) + ' of ' + images.length;
      img.loading = i === 0 ? 'eager' : 'lazy';
      slide.appendChild(img);
      track.appendChild(slide);
    });
    gallery.appendChild(track);

    var dotsWrap = document.createElement('div');
    dotsWrap.className = 'bts-slider__dots';
    var dots = images.map(function (url, i) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'bts-slider__dot';
      dot.setAttribute('aria-label', 'Go to image ' + (i + 1) + ' of ' + images.length);
      dot.addEventListener('click', function () {
        goTo(i);
      });
      dotsWrap.appendChild(dot);
      return dot;
    });

    var prevBtn = makeNav('prev', 'Previous image', 'M12.75 16.2505L6.5 10.0005L12.75 3.75049');
    var nextBtn = makeNav('next', 'Next image', 'M7.5 3.74121L13.75 9.99121L7.5 16.2412');
    gallery.appendChild(prevBtn);
    gallery.appendChild(nextBtn);
    gallery.appendChild(dotsWrap);

    function makeNav(dir, label, path) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bts-slider__nav bts-slider__nav--' + dir;
      btn.setAttribute('aria-label', label);
      btn.innerHTML = '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">' +
        '<path d="' + path + '" stroke="currentColor" stroke-width="1.5" ' +
        'stroke-linecap="round" stroke-linejoin="round"/></svg>';
      btn.addEventListener('click', function () {
        goTo(currentIndex() + (dir === 'next' ? 1 : -1));
      });
      return btn;
    }

    function currentIndex() {
      var w = track.clientWidth || 1;
      return Math.max(0, Math.min(images.length - 1, Math.round(track.scrollLeft / w)));
    }

    function goTo(i) {
      var target = Math.max(0, Math.min(images.length - 1, i));
      track.scrollTo({ left: target * track.clientWidth, behavior: 'smooth' });
      sync(target);
    }

    function sync(i) {
      dots.forEach(function (dot, di) {
        dot.classList.toggle('bts-slider__dot--active', di === i);
        if (di === i) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
      prevBtn.disabled = i === 0;
      nextBtn.disabled = i === images.length - 1;
    }

    // Keep dots/arrows in sync with touch swipes on the scroll-snap track.
    track.addEventListener('scroll', function () {
      sync(currentIndex());
    }, { passive: true });

    sync(0);
  }

  function saveModalDraft(root) {
    var ctx = root._btsModalContext;
    if (!ctx) return;
    var state = ctx.versionEl._btsState;
    state.slots[ctx.index] = { color: ctx.draft.color, size: ctx.draft.size };
    closeModal(root);
    renderSlots(root, ctx.versionEl);
  }

  /* ----------------------------------------------------------------------
   * Version switching (segmented control) — resets all selection state.
   * ------------------------------------------------------------------- */

  function switchVersion(root, handle) {
    if (!handle || handle === root._btsActiveHandle) return;

    var versionEls = qsa(root, '[data-bts-version]');
    var nextEl = null;
    versionEls.forEach(function (el) {
      var isMatch = el.getAttribute('data-version-handle') === handle;
      if (isMatch) nextEl = el;
      el.hidden = !isMatch;
      el.classList.toggle('bts-version--active', isMatch);
    });
    if (!nextEl) return;

    root._btsActiveHandle = handle;
    qsa(root, '[data-bts-version-btn]').forEach(function (btn) {
      setPressed(btn, btn.getAttribute('data-version-handle') === handle, 'bts-version-btn--active');
    });

    closeModal(root);
    resetVersionState(root, nextEl);

    // The dynamic checkout (Shop Pay) button rendered via `{{ form |
    // payment_button }}` is bound server-side to the page's own product —
    // it cannot be retargeted to a linked version's product/variant from
    // JS. Hide it while a non-default version is active rather than let it
    // silently check out the wrong product. See
    // _workspace/03_frontend_output.md for details.
    if (root._btsDynamicCheckout) {
      root._btsDynamicCheckout.hidden = handle !== root._btsDefaultHandle;
    }
  }

  function resetVersionState(root, versionEl) {
    var state = versionEl._btsState;
    if (!state) return;

    // Respect custom.bundle_tier_default (stamped by Liquid as
    // data-default-tier-name) so switching versions and coming back restores
    // the merchant's pre-selected tier, not blindly the first one.
    var defaultTierName = versionEl.getAttribute('data-default-tier-name') || '';
    var firstOffer = (defaultTierName
      ? state.refs.offerButtons.filter(function (btn) {
        return btn.getAttribute('data-tier-name') === defaultTierName;
      })[0]
      : null) || state.refs.offerButtons[0];
    state.refs.offerButtons.forEach(function (btn) {
      setPressed(btn, btn === firstOffer, 'bts-offer--active');
    });
    state.activeOffer = firstOffer;
    state.pairCount = firstOffer ? Number(firstOffer.getAttribute('data-tier-pair-count')) || 1 : 1;
    state.customEnabled = false;
    state.slots = [];
    state.single = { color: state.versionData.defaultColor || '', size: state.versionData.defaultSize || '' };

    state.refs.sharedColorButtons.forEach(function (btn) {
      setPressed(btn, btn.getAttribute('data-color-name') === state.single.color, 'bts-swatch--active');
    });
    updateSharedSizeButtons(versionEl);
    applySharedOptionLabels(root, versionEl);

    if (firstOffer) updateVariantInput(root, firstOffer);
    syncLabelsForActiveOffer(root, versionEl);
    renderCustomizeVisibility(versionEl);
    renderSharedOptionsVisibility(versionEl);
    renderSlots(root, versionEl);
  }

  /* ----------------------------------------------------------------------
   * Product form sync (dynamic checkout / variant id input)
   * ------------------------------------------------------------------- */

  function syncBundlePriceDisplays(root, offerBtn) {
  if (!root || !offerBtn) return;

  var totalPrice = offerBtn.getAttribute('data-tier-price-money');
  var perPairPrice = offerBtn.getAttribute('data-tier-price-per-pair');
  if (!totalPrice || !perPairPrice) return;

  var priceScope = root.closest('[id^="MainProduct-"][data-section]') || root;
  while (priceScope && priceScope !== document.body && !priceScope.querySelector('.price-hhd__main')) {
    priceScope = priceScope.parentElement;
  }
  if (!priceScope || !priceScope.querySelector('.price-hhd__main')) {
    priceScope = document.querySelector('[id^="ProductInfo-"]') ||
      document.querySelector('[id^="MainProduct-"]');
  }
  if (!priceScope || !priceScope.querySelector('.price-hhd__main')) return;

  priceScope.querySelectorAll('.price-hhd__main').forEach(function (el) {
    el.textContent = totalPrice;
  });

  priceScope.querySelectorAll('.price-hhd__badge-text').forEach(function (el) {
    var label = el.getAttribute('data-per-pair-label');
    if (label === null) {
      label = el.textContent.trim().replace(/^[^0-9]*[0-9][0-9.,]*/, '').trim();
    }
    el.textContent = perPairPrice + (label ? ' ' + label : '');
  });

  priceScope.querySelectorAll('[data-bts-summary-bundle] .bts-summary__savings').forEach(function (el) {
    el.textContent = perPairPrice + '/pair';
  });
}
function updateVariantInput(root, offerBtn) {
    var variantId = offerBtn.getAttribute('data-tier-variant-id');
    if (!variantId) return;

syncBundlePriceDisplays(root, offerBtn);
root.dispatchEvent(new CustomEvent('bts:tier-change', {
  bubbles: true,
  detail: {
    variantId: variantId,
    price: offerBtn.getAttribute('data-tier-price-money') || '',
    pricePerPair: offerBtn.getAttribute('data-tier-price-per-pair') || '',
    savings: offerBtn.getAttribute('data-tier-savings-note') || '',
    pairCount: offerBtn.getAttribute('data-tier-pair-count') || '',
    actionLabel: offerBtn.getAttribute('data-tier-action-label') || ''
  }
}));
    var form = root.closest('form[is="product-form"]') ||
      root.closest('form.main-product-form') ||
      root.closest("form[data-type='add-to-cart-form']");
    if (!form) return;
    var input = form.querySelector('input.product-variant-id, input[name="id"]');
    if (!input) return;
    input.value = variantId;
    input.dispatchEvent(new Event('change', { bubbles: true }));

  syncBundlePriceDisplays(root, offerBtn);
  root.dispatchEvent(new CustomEvent('bts:tier-change', {
    bubbles: true,
    detail: {
      variantId: variantId,
      price: offerBtn.getAttribute('data-tier-price-money') || '',
      pricePerPair: offerBtn.getAttribute('data-tier-price-per-pair') || '',
      savings: offerBtn.getAttribute('data-tier-savings-note') || '',
      pairCount: offerBtn.getAttribute('data-tier-pair-count') || '',
      actionLabel: offerBtn.getAttribute('data-tier-action-label') || ''
    }
  }));
  }

  /* ----------------------------------------------------------------------
   * Add to cart
   * ------------------------------------------------------------------- */

  function setMainActionLoading(actionBtn, isLoading) {
    actionBtn.classList.toggle('bts-main-action--loading', isLoading);
  }

  function showTransientLabel(actionBtn, labelEl, text, revertText) {
    if (labelEl) labelEl.textContent = text;
    actionBtn.disabled = false;
    window.setTimeout(function () {
      if (labelEl) labelEl.textContent = revertText;
    }, 1800);
  }

  function openCartDrawer() {
    // Mirrors assets/bundle-box.js's openCartDrawer() so both bundle
    // systems trigger the cart drawer the same way.
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
    var cartDrawerEl = document.querySelector('cart-drawer');
    if (cartDrawerEl && typeof cartDrawerEl.show === 'function') {
      cartDrawerEl.show();
    }
  }

  function addToCart(root, versionEl) {
    var state = versionEl._btsState;
    var btn = state.activeOffer;
    var actionBtn = state.refs.mainActionBtn;
    if (!btn || !actionBtn) return;

    var tierName = btn.getAttribute('data-tier-name');
    var pair1 = state.customEnabled ? state.slots[0] : state.single;
    var pair1Color = (pair1 && pair1.color) || state.single.color;
    var pair1Size = (pair1 && pair1.size) || state.single.size;

    // Use the variant matching Pair 1's actual color/size instead of the
    // arbitrary "representative" variant Liquid picked at page load (see
    // docs/task/bundle-tiered-selector-plan.md section 3.1) — so the cart
    // line's image/title reflect what the customer picked, and availability
    // is checked against that color/size rather than a fixed one. Fall back
    // to the SSR default if no match is found (should not normally happen).
    var matchedVariant = findVariant(state.versionData, pair1Color, pair1Size, tierName);
    var variantId = matchedVariant ? matchedVariant.id : btn.getAttribute('data-tier-variant-id');
    var isAvailable = matchedVariant ? matchedVariant.available : btn.getAttribute('data-tier-available') !== 'false';
    var labelEl = state.refs.mainActionLabelEl;
    var idleLabel = actionBtn.getAttribute('data-add-label');

    if (!variantId || !isAvailable) {
      showTransientLabel(actionBtn, labelEl, actionBtn.getAttribute('data-unavailable-label'), idleLabel);
      return;
    }

    var properties = {};

    // `offer` is an existing theme convention: sections/cart-drawer.liquid hides the
    // variant's own option values whenever a line item carries it, and prints this
    // value under the product title instead. Pair 1's real variant may still show
    // a color/size that differs from later pairs, so this stays needed even with
    // the findVariant() match above.
    if (tierName) properties['offer'] = tierName;

    for (var i = 1; i <= state.pairCount; i += 1) {
      var pair = state.customEnabled ? state.slots[i - 1] : state.single;
      var colorValue = (pair && pair.color) || state.single.color;
      var sizeValue = (pair && pair.size) || state.single.size;
      properties['Pair ' + i] = colorValue + ' / ' + sizeValue;
    }

    actionBtn.disabled = true;
    setMainActionLoading(actionBtn, true);
    if (labelEl) labelEl.textContent = actionBtn.getAttribute('data-adding-label');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1, properties: properties }]
      })
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().catch(function () {
            return null;
          }).then(function (payload) {
            var message = (payload && payload.description) || 'Add to cart failed';
            throw new Error(message);
          });
        }
        return response.json();
      })
      .then(function () {
        setMainActionLoading(actionBtn, false);
        if (labelEl) labelEl.textContent = actionBtn.getAttribute('data-added-label');
        openCartDrawer();
        window.setTimeout(function () {
          actionBtn.disabled = !isAvailable;
          if (labelEl) labelEl.textContent = actionBtn.getAttribute('data-add-label');
        }, 1500);
      })
      .catch(function () {
        setMainActionLoading(actionBtn, false);
        showTransientLabel(actionBtn, labelEl, actionBtn.getAttribute('data-unavailable-label'), idleLabel);
      });
  }
})();